import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { Gavi } from '#components/mobile/travesia/Gavi';
import { Boton } from '#components/mobile/ui/Boton';
import { Importe } from '#components/mobile/ui/Importe';
import { Tarjeta } from '#components/mobile/ui/Tarjeta';
import { color, espacio, texto } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';

type PasoHechoProps = {
  nuevos: number;
  sinCategoria: number;
  nombreCuenta: string;
  /** Céntimos. */
  saldo: number;
  /** La pregunta «mismo comercio» o su aviso, bajo el resumen. */
  mismoComercio?: ReactNode;
  /** Colocados por «mismo comercio» (con historia, se suman a lo que revisar). */
  colocados?: number;
  onRevisar: () => void;
  onMasTarde: () => void;
  /** Sin él (al traer la historia) no sale «Deshacer la importación». */
  onDeshacer?: () => void;
  /** Movimientos traídos como historia. */
  historia?: number;
  /** Apuntados a mano sustituidos por el del banco («Es el mismo»). */
  sustituidos?: number;
};

/** Paso 4: cuántos han entrado y adónde ir ahora. */
export function PasoHecho({
  nuevos,
  sinCategoria,
  nombreCuenta,
  saldo,
  mismoComercio,
  colocados = 0,
  onRevisar,
  onMasTarde,
  onDeshacer,
  historia = 0,
  sustituidos = 0,
}: PasoHechoProps) {
  const { t } = useTranslation();
  const conCategoria = nuevos - sinCategoria;
  // Todo lo nuevo espera en «Por revisar», tenga categoría o no; lo de la
  // historia colocado por comercio, también.
  const porRevisar = nuevos + (historia > 0 ? colocados : 0);

  return (
    <View
      style={{ flex: 1, gap: 18, padding: espacio.margen }}
      data-testid="importar-hecho"
      data-historia={historia > 0}
    >
      <View
        style={{
          alignSelf: 'center',
          width: 140,
          height: 140,
          borderRadius: 70,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: color.accentSoft,
          marginTop: 24,
        }}
      >
        <Gavi estado="aplaude" ancho={96} />
      </View>

      <View
        style={{ alignItems: 'center', gap: 6 }}
        data-testid="importar-hecho-texto"
      >
        <Text style={{ ...texto.display, textAlign: 'center' }}>
          {historia > 0 && nuevos === 0
            ? t('{{count}} transactions of history on board!', {
                count: historia,
              })
            : t('{{count}} transactions on board!', { count: nuevos })}
        </Text>
        {historia > 0 && nuevos > 0 && (
          <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
            {t('And {{count}} of history.', { count: historia })}
          </Text>
        )}
        {sustituidos > 0 && (
          <Text
            style={{ ...texto.cuerpo, color: color.fg2, textAlign: 'center' }}
            data-testid="importar-sustituidos"
          >
            {t(
              '{{count}} of them replace ones you entered yourself: your name is in their notes.',
              { count: sustituidos },
            )}
          </Text>
        )}
        <Text
          style={{
            ...texto.cuerpo,
            color: color.fg2,
            textAlign: 'center',
            padding: '0 12px',
          }}
        >
          {historia > 0 && nuevos === 0
            ? t(
                'They count in Reports and in what I tell you. Your balance and your months with Rumbo stay as they were.',
              )
            : sinCategoria === 0
              ? t(
                  'All of them already have their category: you just have to approve them in To review.',
                )
              : conCategoria === 0
                ? t(
                    "They are waiting in To review: tell me what they are and I'll learn for next time.",
                  )
                : t(
                    "They are waiting in To review: {{done}} already have their category and the other {{count}} don't. Tell me what they are and I'll learn for next time.",
                    { done: conCategoria, count: sinCategoria },
                  )}
        </Text>
      </View>

      {mismoComercio}

      <Tarjeta style={{ gap: 2 }} data-testid="importar-saldo">
        <Text style={{ ...texto.secundario, color: color.fg2 }}>
          {t('{{account}} balance', { account: nombreCuenta })}
        </Text>
        <PrivacyFilter>
          <Importe valor={saldo} style={{ ...texto.cifra, fontSize: 20 }} />
        </PrivacyFilter>
      </Tarjeta>

      <View style={{ flex: 1 }} />

      {porRevisar > 0 && (
        <Boton bloque onPress={onRevisar} data-testid="importar-revisar">
          {t('Review the {{count}} now', { count: porRevisar })}
        </Boton>
      )}
      <Boton
        bloque
        variante={porRevisar > 0 ? 'fantasma' : 'primario'}
        onPress={onMasTarde}
        data-testid="importar-mas-tarde"
      >
        {porRevisar > 0 ? t('Later') : t('Done')}
      </Boton>
      {onDeshacer && (
        <Button
          variant="bare"
          onPress={onDeshacer}
          data-testid="importar-deshacer"
          style={{
            alignSelf: 'center',
            minHeight: 44,
            ...texto.secundario,
            color: color.fg2,
          }}
        >
          <Trans>Undo the import</Trans>
        </Button>
      )}
    </View>
  );
}
